import { useQuery } from '@tanstack/react-query';
import { fetchEngagementSnapshots } from '../services/outreach/engagementAnalyticsService';
import {
  EngagementTimeRange,
  getDaysFromRange,
  roundToTwo,
} from '../utils/engagementAnalytics';

export interface CampaignPerformanceData {
  id: string;
  name: string;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  openRate: number;
  clickRate: number;
  engagementRate: number;
  deliveryRate: number;
  createdAt: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

const buildPerformanceDataset = async (
  limit: number,
  timeRange: EngagementTimeRange
): Promise<CampaignPerformanceData[]> => {
  const snapshots = await fetchEngagementSnapshots({
    includeStats: true,
    includeTracking: false,
  });

  const dataset: CampaignPerformanceData[] = [];
  const days = getDaysFromRange(timeRange);
  const cutoff = Date.now() - days * DAY_MS;

  snapshots.forEach(({ campaign, stats }) => {
    if (!campaign.id || !campaign.name || !stats) {
      return;
    }

    const createdAt = campaign.createdAt ? Date.parse(campaign.createdAt) : NaN;
    if (Number.isFinite(createdAt) && createdAt < cutoff) {
      return;
    }

    const delivered = stats.delivered ?? 0;
    const sent = stats.totalSent ?? delivered;
    const opened = stats.opened ?? 0;
    const clicked = stats.clicked ?? 0;

    dataset.push({
      id: campaign.id,
      name: campaign.name,
      sent,
      delivered,
      opened,
      clicked,
      bounced: stats.bounced ?? 0,
      complained: stats.complained ?? 0,
      openRate: delivered > 0 ? roundToTwo((opened / delivered) * 100) : 0,
      clickRate: delivered > 0 ? roundToTwo((clicked / delivered) * 100) : 0,
      engagementRate:
        delivered > 0 ? roundToTwo(((opened + clicked) / delivered) * 100) : 0,
      deliveryRate: sent > 0 ? roundToTwo((delivered / sent) * 100) : 0,
      createdAt: campaign.createdAt ?? new Date().toISOString(),
    });
  });

  return dataset.sort((a, b) => b.engagementRate - a.engagementRate).slice(0, limit);
};

export const useTopPerformingCampaigns = (
  limit = 5,
  timeRange: EngagementTimeRange = '30d'
) =>
  useQuery({
    queryKey: ['top-performing-campaigns', limit, timeRange],
    queryFn: () => buildPerformanceDataset(limit, timeRange),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
